import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "../utils/msalConfig";
import styles from "./Meeting.module.css";
import { updateTeamsMeeting, getEventAttendees  } from '../services/teamsService';
import { meetingsAPI } from '../services/apiService';
import { Calendar, Clock, Users, Info, Check, MapPin } from 'lucide-react';
import dayjs from "dayjs";

const msalInstance = new PublicClientApplication(msalConfig);

const Meeting = () => {
  const { accounts } = useMsal();
  const [meetings, setMeetings] = useState([]);
  const [rsvpStatus, setRsvpStatus] = useState(null);
  const [userCurrentRSVP, setUserCurrentRSVP] = useState(null);
  const [selectedInstances, setSelectedInstances] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);

  useEffect(() => {
    const fetchMeetings = async () => {
      setIsLoadingEvents(true); // Set loading to true at the start
      try {
        const data = await meetingsAPI.getMeetings();
        
        const groupedMeetings = data.reduce((acc, meeting) => {
          const getDurationInMinutes = (start, end) => {
            const startTime = dayjs(`${meeting.date} ${start}`); 
            const endTime = dayjs(`${meeting.date} ${end}`);
            return endTime.diff(startTime, "minute");
          };
        
  
          const baseTitle = meeting.title.replace(/\s+\d+$/, '');
          if (!acc[baseTitle]) {
            acc[baseTitle] = {
              baseTitle: baseTitle,
              description: meeting.description,
              maxParticipants: meeting.max_participants,
              rsvp_room: meeting.rsvp_list[0].name,
              organizer: meeting.organizer.email,
              duration: getDurationInMinutes(meeting.start_time, meeting.end_time),
              instances: []
            };
          }
          acc[baseTitle].instances.push(meeting);
          return acc;
        }, {});

        Object.values(groupedMeetings).forEach(series => {
          series.instances.sort((a, b) => {
            const dateA = new Date(`${a.date} ${a.start_time}`);
            const dateB = new Date(`${b.date} ${b.start_time}`);
            return dateA - dateB;
          });
        });

        const sortedMeetings = Object.values(groupedMeetings).sort((a, b) => {
          const earliestA = new Date(`${a.instances[0].date} ${a.instances[0].start_time}`);
          const earliestB = new Date(`${b.instances[0].date} ${b.instances[0].start_time}`);
          return earliestB - earliestA; // Reverse sort to show latest first
        });

        setMeetings(sortedMeetings);
        
        // Find user's current RSVP if there is any
        if (accounts[0]?.username) {
          const userEmail = accounts[0].username;
          for (const series of Object.values(groupedMeetings)) {
            for (const instance of series.instances) {
              if (instance.rsvp_list.some(rsvp => rsvp.email === userEmail)) {
                setUserCurrentRSVP({
                  seriesTitle: series.baseTitle,
                  instanceId: instance.id
                });
                break;
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch meetings:", error);
      } finally {
        setIsLoadingEvents(false); // Set loading to false when done
      }
    };

    fetchMeetings();
  }, [accounts]);

  const toggleMeetingExpand = (baseTitle) => {
    setExpandedMeetings(prev => ({
      ...prev,
      [baseTitle]: !prev[baseTitle]
    }));
  };

  const isRSVPDisabled = (meeting) => {
    // Checka if user already RSVP'd in any instance of the meeting
    return meeting.instances.some(instance => 
      instance.rsvp_list.some(rsvp => rsvp.email === accounts[0]?.username)
    );
  };

  const formatToLocalTime = (date, time) => {
    return `${date}T${time}:00Z`;
  };

  // const isRSVPDisabled = (meeting) => {
  //   // Check if user already RSVP'd
  //   const hasUserRSVPd = meeting.rsvp_list.some(
  //     rsvp => rsvp.email === accounts[0]?.username
  //   );
    
  //   // Check if meeting is at max capacity
  //   const isAtMaxCapacity = meeting.rsvp_list.length >= meeting.max_participants;
    
  //   return hasUserRSVPd || isAtMaxCapacity;
  // };

  const getRSVPButtonText = (instance, seriesTitle, userCurrentRSVP) => {
    if (!instance) return "RSVP";
    
    if (instance.rsvp_list.length >= instance.max_participants) {
      return "Meeting Full";
    }

    if (instance.rsvp_list.some(rsvp => rsvp.email === accounts[0]?.username)) {
      return "RSVP'd";
    }

    const hasRSVPInSeries = seriesTitle.instances.some(inst => 
      inst.rsvp_list.some(rsvp => rsvp.email === accounts[0]?.username)
    );

    if (hasRSVPInSeries) {
      return "Switch RSVP to this";
    }
    
    return "RSVP";
  };


  // Handle RSVP
  const handleRSVPAndCalendar  = async (newMeeting) => {
    if (!accounts || accounts.length === 0) {
      setRsvpStatus("Please log in to RSVP for the meeting.");
      return;
    }

    setIsLoading(true);

    
    const userEmail = accounts[0].username;
    const userName = accounts[0].name;

    try {
      if (userCurrentRSVP) {
        const newMeetingSeries = newMeeting.title.replace(/\s+\d+$/, '');

        
        // Find the current meeting series using the same title cleaning logic
        const currentSeries = meetings.find(series => 
          series.baseTitle.replace(/\s+\d+$/, '') === newMeetingSeries
        );

        if (currentSeries) {

          const currentMeeting = currentSeries.instances.find(instance => 
            instance.rsvp_list.some(rsvp => rsvp.email === userEmail)
          );
          if (currentMeeting) {
            await meetingsAPI.cancelRSVP(currentMeeting.id, { email: userEmail, name: userName });

            // Create a copy of the meeting with the user removed from the RSVP list
            const updatedCurrentMeeting = {
              ...currentMeeting,
              rsvp_list: currentMeeting.rsvp_list.filter(attendee => attendee.email !== userEmail)
            };

            // Update Teams calendar for the old meeting with the user removed
            await updateTeamsMeeting(updatedCurrentMeeting, accounts, currentMeeting.id_meeting);
          }
        }
      }

      const attendees_ms = await getEventAttendees(newMeeting.accessToken, newMeeting.id_meeting)
      const rsvpPayload = {
        rsvp_list: attendees_ms
      };
      rsvpPayload.rsvp_list.push({ email: userEmail, name: userName });

      
      const updatedMeeting = await meetingsAPI.rsvpToMeeting(newMeeting.id, rsvpPayload);

      // Update meetings state and user's current RSVP
      setMeetings(prevMeetings => 
        prevMeetings.map(series => ({
          ...series,
          instances: series.instances.map(instance => 
            instance.id === updatedMeeting.id ? updatedMeeting : instance
          )
        }))
      );
      setUserCurrentRSVP({
        seriesTitle: newMeeting.title.replace(/\s+\d+$/, ''),
        instanceId: newMeeting.id
      });

      const newRSVP = {
        email: accounts[0].username,
        name: accounts[0].name
      };
      
    // updatedMeeting.rsvp_list.push(newRSVP);
      await updateTeamsMeeting(updatedMeeting, accounts, updatedMeeting.id_meeting);
      setRsvpStatus("Successfully RSVP'd and added to your calendar!");
    } catch (error) {
      console.error("Error in RSVP and calendar process:", error);
      setRsvpStatus(`An error occurred: ${error.message}`);
    }
    finally {
      // Set loading state back to false when operation completes
      setIsLoading(false);
    }
  };

  const handleInstanceSelect = (seriesTitle, instanceId) => {
    setSelectedInstances(prev => ({
      ...prev,
      [seriesTitle]: instanceId
    }));
  };

    return (
      <div 
        className="min-h-screen p-8 text-white"
        style={{
          background: `url('https://redrocket.energy/wp-content/uploads/2024/05/RR_Resting-Rocket.webp?id=3023') no-repeat center center fixed`,
          backgroundSize: 'cover',
          backgroundColor: 'black'
        }}
      >
        <h1 className="text-4xl font-avenir font-900 text-center mb-8 ">Available Events</h1>
        
        {rsvpStatus && (
          <p className="text-center mb-6 text-lg text-yellow-400">{rsvpStatus}</p>
        )}
        {isLoadingEvents ? (
          <div className="flex flex-col items-center justify-center my-16">
            <div className="w-16 h-16 border-4 border-t-transparent border-white rounded-full animate-spin mb-4"></div>
            <p className="text-xl font-avenir text-white">Loading events...</p>
          </div>
        ) : meetings.length === 0 ? (
          <div className="text-center my-16">
            <p className="text-xl font-avenir text-white">No events are currently available</p>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full auto-rows-fr">
          {meetings.map((meetingSeries, index) => {
            const selectedInstanceId = selectedInstances[meetingSeries.baseTitle] || '';
            const selectedInstance = meetingSeries.instances.find(
              instance => instance.id.toString() === selectedInstanceId
            );
  
            return (
              <div 
                key={meetingSeries.baseTitle} 
                className="rounded-lg transition-all duration-500 hover:-translate-y-2 hover:shadow-lg 
                border-2 border-black w-full max-w-[780px] backdrop-blur-sm flex flex-col 
                mx-auto h-full"
                >
                <div className="p-6 ">
                  <div className="flex items-center justify-center gap-2 text-center">
                    <h3 className="text-xl font-bold text-white font-avenir">{meetingSeries.baseTitle}</h3>
                  </div>
                  
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-3 mb-5">
                    <div className="flex items-center gap-2 bg-black/30 rounded-full px-3 py-1">
                      <Users className="w-4 h-4 text-white" />
                      <span className="text-sm text-white font-avenir">
                        Max {meetingSeries.maxParticipants} attendees
                      </span>
                    </div>
                    <div className="flex items-center gap-2 bg-black/30 rounded-full px-3 py-1">
                      <Clock className="w-4 h-4 text-white" />
                      <span className="text-sm text-white font-avenir">{meetingSeries.duration} minutes</span>
                    </div>
                    <div className="flex items-center gap-2 bg-black/30 rounded-full px-3 py-1">
                      <MapPin className="w-4 h-4 text-white" />
                      <span className="text-sm text-white font-avenir">{meetingSeries.rsvp_room}</span>
                    </div>
                  </div>
                  <div className="h-1 bg-black my-2"></div>
                </div>
  
                <div className="p-6">
                  <div className="flex items-start gap-3 text-white">
                    <Info className="w-5 h-5 mt-1 flex-shrink-0" />
                    <p className="text-md leading-relaxed text-white opacity-100 font-avenir">{meetingSeries.description}</p>
                  </div>
                </div>
  
                <div className="px-6 pb-6">
                  <label className="block text-md font-medium text-white mb-3 font-avenir">
                    Choose a Time Slot:
                  </label>
                  <div className="space-y-4">
                    {meetingSeries.instances.map((instance) => (
                      <label
                        key={instance.id}
                        className={`flex items-center gap-3 p-4 border border-black rounded-xl cursor-pointer transition
                          ${selectedInstanceId === instance.id.toString()
                            ? "bg-black/40"
                            : "hover:bg-black/20"
                          } ${instance.rsvp_list.length >= instance.max_participants ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <input
                          type="radio"
                          id={`instance-${instance.id}`}
                          name={`meeting-${meetingSeries.baseTitle}`}
                          value={instance.id}
                          checked={selectedInstanceId === instance.id.toString()}
                          onChange={() =>
                            handleInstanceSelect(meetingSeries.baseTitle, instance.id.toString())
                          }
                          disabled={instance.rsvp_list.length >= instance.max_participants}
                          className="hidden"
                        />
                        <div
                          className={`w-5 h-5 flex items-center justify-center border-2 rounded-full 
                            ${selectedInstanceId === instance.id.toString()
                              ? "border-green-600 bg-green-600"
                              : "border-gray-400 bg-white"
                            }`}
                        >
                          {selectedInstanceId === instance.id.toString() && (
                            <Check className="w-3 h-3 text-black" />
                          )}
                        </div>
                        <Clock className="w-5 h-5 text-white" />
                        <span className="text-md font-medium text-white font-avenir">
                          {new Date(instance.date).toLocaleDateString("en-US", {
                            weekday: "long",
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}{" "}
                          | {instance.start_time} - {instance.end_time} |{" "}
                          <span className="font-avenir">
                            Spots: {instance.rsvp_list.length}/{instance.max_participants}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
  
                  <div className="mt-6 flex space-x-4">
                    <button
                      onClick={() => handleRSVPAndCalendar(selectedInstance)}
                      className={`w-full py-3 px-4 rounded-lg transition-all duration-300 font-avenir
                        ${!selectedInstance || selectedInstance?.rsvp_list.length >= meetingSeries.maxParticipants || selectedInstance?.rsvp_list.some(rsvp => rsvp.email === accounts[0]?.username)
                          ? "bg-gray-600 cursor-not-allowed"
                          : selectedInstance?.rsvp_list.some(rsvp => rsvp.email === accounts[0]?.username)
                          ? "bg-green-600 hover:bg-green-700"
                          : "bg-black hover:bg-[#ff0404] hover:-translate-y-1 hover:shadow-lg"
                        }`}
                      disabled={!selectedInstance || 
                        selectedInstance?.rsvp_list.length >= meetingSeries.maxParticipants || 
                        selectedInstance?.rsvp_list.some(rsvp => rsvp.email === accounts[0]?.username) || 
                        isLoading}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                          <span>Processing...</span>
                        </div>
                      ) : (
                        getRSVPButtonText(selectedInstance, meetingSeries, userCurrentRSVP)
                      )}
                    </button>

                    <a
                      href={`mailto:${meetingSeries.organizer}`}
                      className="w-full py-3 px-4 rounded-lg transition-all duration-300 font-avenir text-center
                        bg-rocketBlack hover:bg-rocketBlack hover:-translate-y-1 hover:shadow-lg text-white flex items-center justify-center"
                    >
                      Email Organizer
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
    );
  };
  export default Meeting;